import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { uploadApi, type UploadIdentifiedCarApi } from '../../api/client';
import { extractLocationFromExif } from '../../services/extractExifLocation';
import { CarIdentification } from '../../types';
import { CAR_TYPES, PERFORMANCE_TYPES } from '../../constants/spotCategories';
import { COUNTRIES_US_FIRST } from '../../constants/countries';
import { useCarMakes } from '../../hooks/useCarMakes';
import { trackEvent } from '../../analytics';
import styles from './SpotUpload.module.css';

const CURRENT_MAKE_VALUE = '__current__';
const OTHER_MAKE_VALUE = '__other__';

function formatIdentifiedYearRange(identifiedCar: UploadIdentifiedCarApi) {
  if (typeof identifiedCar.year === 'number') return String(identifiedCar.year);
  return identifiedCar.yearRange ?? '';
}

export function SpotUpload() {
  const navigate = useNavigate();
  const { user, isAuthenticated, getUsers, loadUsers } = useAuth();
  const { addSpot, spots } = useData();
  const users = getUsers();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const hasTrackedStartRef = useRef(false);

  const MAX_IMAGES = 4;
  type ImageEntry = { preview: string; file: File };
  const [imageEntries, setImageEntries] = useState<ImageEntry[]>([]);
  const [car, setCar] = useState<CarIdentification & { mods?: string; wheels?: string; badges?: string; userNotes?: string }>({
    make: '',
    model: '',
    yearRange: '',
    generation: '',
    bodyStyle: '',
    performanceType: '',
    trim: '',
    mods: '',
    wheels: '',
    badges: '',
    userNotes: '',
  });
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [city, setCity] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [event, setEvent] = useState('');
  const [taggedOwnerId, setTaggedOwnerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [identityStatus, setIdentityStatus] = useState<'idle' | 'loading' | 'found' | 'not-found' | 'error'>('idle');
  const [identityMessage, setIdentityMessage] = useState('');
  const [useCustomMake, setUseCustomMake] = useState(false);
  const autoFilledIdentityRef = useRef<{ make: string; model: string; trim?: string; generation?: string; yearRange?: string; bodyStyle?: string; performanceType?: string } | null>(null);
  const lastIdentifiedFileRef = useRef<File | null>(null);
  const mySpotCount = user ? spots.filter((spot) => spot.authorId === user.id).length : 0;
  const { carMakes, loading: carMakesLoading, error: carMakesError } = useCarMakes();

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const trackSpotStart = useCallback((source: string) => {
    if (hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;
    const isFirstSpot = mySpotCount === 0;
    trackEvent('spot_creation_started', { source, first_spot: isFirstSpot });
    if (isFirstSpot) {
      trackEvent('first_spot_started', { source });
    }
  }, [mySpotCount]);

  const applyIdentifiedCar = useCallback((identifiedCar: UploadIdentifiedCarApi) => {
    setCar((prev) => {
      const previousAutoFilled = autoFilledIdentityRef.current;
      const canReplaceMake = !prev.make || (previousAutoFilled != null && prev.make === previousAutoFilled.make);
      const canReplaceModel = !prev.model || (previousAutoFilled != null && prev.model === previousAutoFilled.model);
      const canReplaceTrim = !prev.trim || (previousAutoFilled != null && prev.trim === previousAutoFilled.trim);
      const canReplaceGeneration = !prev.generation || (previousAutoFilled != null && prev.generation === previousAutoFilled.generation);
      const canReplaceYearRange = !prev.yearRange || (previousAutoFilled != null && prev.yearRange === previousAutoFilled.yearRange);
      const canReplaceBodyStyle = !prev.bodyStyle || (previousAutoFilled != null && prev.bodyStyle === previousAutoFilled.bodyStyle);
      const canReplacePerformanceType = !prev.performanceType || (previousAutoFilled != null && prev.performanceType === previousAutoFilled.performanceType);
      const detectedYearRange = formatIdentifiedYearRange(identifiedCar);
      const nextMake = canReplaceMake ? identifiedCar.make : prev.make;
      const nextModel = canReplaceModel ? identifiedCar.model : prev.model;
      const nextTrim = canReplaceTrim ? (identifiedCar.trim ?? prev.trim) : prev.trim;
      const nextGeneration = canReplaceGeneration ? (identifiedCar.generation ?? prev.generation) : prev.generation;
      const nextYearRange = canReplaceYearRange ? (detectedYearRange || prev.yearRange) : prev.yearRange;
      const nextBodyStyle = canReplaceBodyStyle ? (identifiedCar.bodyStyle ?? prev.bodyStyle) : prev.bodyStyle;
      const nextPerformanceType = canReplacePerformanceType ? (identifiedCar.performanceType ?? prev.performanceType) : prev.performanceType;
      if (canReplaceMake || canReplaceModel || canReplaceTrim || canReplaceGeneration || canReplaceYearRange || canReplaceBodyStyle || canReplacePerformanceType) {
        autoFilledIdentityRef.current = {
          make: canReplaceMake ? identifiedCar.make : nextMake,
          model: canReplaceModel ? identifiedCar.model : nextModel,
          trim: canReplaceTrim ? identifiedCar.trim ?? nextTrim : nextTrim,
          generation: canReplaceGeneration ? identifiedCar.generation ?? nextGeneration : nextGeneration,
          yearRange: canReplaceYearRange ? detectedYearRange || nextYearRange : nextYearRange,
          bodyStyle: canReplaceBodyStyle ? identifiedCar.bodyStyle ?? nextBodyStyle : nextBodyStyle,
          performanceType: canReplacePerformanceType ? identifiedCar.performanceType ?? nextPerformanceType : nextPerformanceType,
        };
      }
      const next = {
        ...prev,
        make: nextMake,
        model: nextModel,
        trim: nextTrim,
        generation: nextGeneration,
        yearRange: nextYearRange,
        bodyStyle: nextBodyStyle,
        performanceType: nextPerformanceType,
      };
      return next;
    });
  }, []);

  const addFiles = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    const list = Array.from(fileList).filter((f) => f.type.startsWith('image/')).slice(0, MAX_IMAGES - imageEntries.length);
    if (list.length === 0) return;

    const wasEmpty = imageEntries.length === 0;
    setImageEntries((prev) => [
      ...prev,
      ...list.map((f) => ({ preview: URL.createObjectURL(f), file: f })),
    ]);
    setError('');
    if (wasEmpty) {
      setCaption('');
      setLocation('');
      setCity('');
      setCountryCode('');
    }
    if (wasEmpty && list[0]) {
      try {
        const exifLocation = await extractLocationFromExif(list[0]);
        if (exifLocation.location) {
          setLocation(exifLocation.location);
          if (exifLocation.city) setCity(exifLocation.city);
        }
      } catch {
        // EXIF optional
      }
    }
  };

  const coverFile = imageEntries[0]?.file ?? null;

  useEffect(() => {
    if (!coverFile) {
      lastIdentifiedFileRef.current = null;
      setIdentityStatus('idle');
      setIdentityMessage('');
      return;
    }
    if (lastIdentifiedFileRef.current === coverFile) {
      return;
    }

    lastIdentifiedFileRef.current = coverFile;
    let cancelled = false;
    setIdentityStatus('loading');
    setIdentityMessage('');

    void uploadApi.identifySpot(coverFile)
      .then((result) => {
        if (cancelled) return;
        if (result.identifiedCar) {
          applyIdentifiedCar(result.identifiedCar);
          setIdentityStatus('found');
          const details = [
            result.identifiedCar.trim,
            result.identifiedCar.generation,
            formatIdentifiedYearRange(result.identifiedCar),
            result.identifiedCar.bodyStyle,
            result.identifiedCar.performanceType,
          ].filter(Boolean).join(', ');
          setIdentityMessage([
            [result.identifiedCar.make, result.identifiedCar.model].filter(Boolean).join(' '),
            details ? `(${details})` : '',
          ].filter(Boolean).join(' '));
          setError('');
          return;
        }
        setIdentityStatus('not-found');
        setIdentityMessage("Couldn't confidently identify the cover photo.");
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Could not identify the cover photo.';
        setIdentityStatus('error');
        setIdentityMessage(message);
        if (message.includes('contain a car')) {
          setError(message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applyIdentifiedCar, coverFile]);

  const moveImage = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= imageEntries.length) return;
    setImageEntries((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const removeImage = (index: number) => {
    setImageEntries((prev) => {
      const entry = prev[index];
      if (entry?.preview.startsWith('blob:')) URL.revokeObjectURL(entry.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated || !user) {
      setError('Please sign in to post a spot.');
      return;
    }
    if (imageEntries.length === 0) {
      setError('Please select at least one photo.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const filesToUpload = imageEntries.map((e) => e.file);
      const { files: uploaded, identifiedCar } = await uploadApi.spot(filesToUpload);
      const selectedMakeId = carMakes.find((carMake) => carMake.name.toLowerCase() === car.make.trim().toLowerCase())?.id;
      const resolvedMake = car.make || identifiedCar?.make || '';
      const resolvedModel = car.model || identifiedCar?.model || '';
      const resolvedTrim = car.trim || identifiedCar?.trim || undefined;
      const resolvedYearRange = car.yearRange || (identifiedCar ? formatIdentifiedYearRange(identifiedCar) : '') || undefined;
      const resolvedBodyStyle = car.bodyStyle || identifiedCar?.bodyStyle || '';
      const resolvedPerformanceType = car.performanceType || identifiedCar?.performanceType || '';
      if (!resolvedMake || !resolvedModel) {
        setError('Please enter at least make and model.');
        return;
      }
      if (!resolvedBodyStyle || !resolvedPerformanceType) {
        setError('Please select both car type and performance type.');
        return;
      }
      const spot = await addSpot({
        authorId: user.id,
        images: uploaded,
        car: {
          makeId: useCustomMake ? undefined : (selectedMakeId || undefined),
          make: resolvedMake,
          model: resolvedModel,
          generation: car.generation || identifiedCar?.generation || undefined,
          yearRange: resolvedYearRange,
          bodyStyle: resolvedBodyStyle,
          performanceType: resolvedPerformanceType,
          trim: resolvedTrim,
          mods: car.mods || undefined,
          wheels: car.wheels || undefined,
          badges: car.badges || undefined,
          userNotes: car.userNotes || undefined,
        },
        caption: caption || undefined,
        location: location || undefined,
        city: city || undefined,
        countryCode: countryCode || undefined,
        event: event || undefined,
        taggedOwnerId: taggedOwnerId || undefined,
      });
      const isFirstSpot = mySpotCount === 0;
      trackEvent('spot_creation_completed', {
        first_spot: isFirstSpot,
        image_count: imageEntries.length,
        has_location: !!location,
        tagged_owner: !!taggedOwnerId,
      });
      if (isFirstSpot) {
        trackEvent('first_spot_completed', {
          image_count: imageEntries.length,
        });
      }
      navigate(`/spot/${spot.id}`, { state: { fromSpotForm: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <h1>Spot a car</h1>
        <div className={styles.gateCard}>
          <p className={styles.signInPrompt}>
            Create an account to post sightings, react to spots, and start building your RideSpot reputation.
          </p>
          <div className={styles.gateActions}>
            <Link
              to="/create-account"
              className={styles.cta}
              onClick={() => trackEvent('cta_click', { context: 'spot_upload_gate', label: 'create_account' })}
            >
              Create account
            </Link>
            <Link
              to="/login"
              className={styles.secondaryCta}
              onClick={() => trackEvent('cta_click', { context: 'spot_upload_gate', label: 'sign_in' })}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedMake = carMakes.find((carMake) => carMake.name.toLowerCase() === car.make.trim().toLowerCase());
  const makeSelectValue = selectedMake?.id ?? (car.make ? CURRENT_MAKE_VALUE : '');

  return (
    <div className={styles.page}>
      <h1>Spot a car</h1>
      <p className={styles.subtitle}>
        Add photos (up to {MAX_IMAGES}), use the arrows to set order — the first photo is the cover. Then enter the car details below.
      </p>

      <form onSubmit={handleSubmit} className={styles.form} onFocusCapture={() => trackSpotStart('form_focus')}>
        <div className={styles.uploadSection}>
          <h2 className={styles.uploadHeading}>Photos</h2>
          <span className={styles.hint}>Large images are resized on upload.</span>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className={styles.fileInput}
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {imageEntries.length < MAX_IMAGES && (
            <button
              type="button"
              className={styles.addPhotosBtn}
              onClick={() => {
                trackSpotStart('photo_picker');
                photoInputRef.current?.click();
              }}
            >
              {imageEntries.length === 0 ? `Choose photos (up to ${MAX_IMAGES})` : `Add photos (${imageEntries.length}/${MAX_IMAGES})`}
            </button>
          )}
          {imageEntries.length > 0 && (
            <>
              <div className={styles.imageList}>
                {imageEntries.map((entry, i) => (
                  <div key={entry.preview} className={styles.imageEntry}>
                    <div className={styles.imageThumbWrap}>
                      <img src={entry.preview} alt="" className={styles.imageThumb} />
                      <div className={styles.imageActions}>
                        <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label="Move up">
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(i, 1)}
                          disabled={i === imageEntries.length - 1}
                          aria-label="Move down"
                        >
                          ↓
                        </button>
                        <button type="button" className={styles.removePhoto} onClick={() => removeImage(i)} aria-label="Remove">
                          ×
                        </button>
                      </div>
                      {i === 0 && <span className={styles.coverBadge}>Cover</span>}
                    </div>
                  </div>
                ))}
              </div>
              <label className={styles.captionLabel}>
                Caption
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Add a caption for your spot..."
                  className={styles.captionInput}
                />
              </label>
            </>
          )}
        </div>

        <div className={styles.section}>
          <h2>Car details</h2>
          {imageEntries.length > 0 && (
            <div
              className={[
                styles.identityStatus,
                identityStatus === 'loading' ? styles.identityStatusLoading : '',
                identityStatus === 'found' ? styles.identityStatusSuccess : '',
                identityStatus === 'not-found' ? styles.identityStatusNeutral : '',
                identityStatus === 'error' ? styles.identityStatusError : '',
              ].filter(Boolean).join(' ')}
            >
              <span className={styles.identityStatusIndicator} aria-hidden="true" />
              <div className={styles.identityStatusText}>
                <strong className={styles.identityStatusTitle}>
                  {identityStatus === 'loading' && 'Identifying car from cover photo'}
                  {identityStatus === 'found' && 'Car identified'}
                  {identityStatus === 'not-found' && 'Couldn’t identify this car confidently'}
                  {identityStatus === 'error' && 'Car identification unavailable'}
                  {identityStatus === 'idle' && 'Cover photo car identification'}
                </strong>
                <span className={styles.identityStatusMessage}>
                  {identityStatus === 'loading' && 'Analyzing the first photo now and auto-filling car details when possible.'}
                  {identityStatus === 'found' && `Cover photo detected as ${identityMessage}.`}
                  {identityStatus === 'not-found' && "You can still fill in the make and model manually below."}
                  {identityStatus === 'error' && identityMessage}
                  {identityStatus === 'idle' && 'The cover photo is used to auto-fill make and model when possible.'}
                </span>
              </div>
            </div>
          )}
          <div className={styles.grid}>
            <label>
              Make *
              {(useCustomMake || carMakesError) ? (
                <>
                  {!carMakesError && (
                    <button
                      type="button"
                      className={styles.selectMakeLink}
                      onClick={() => {
                        const matchedMake = carMakes.find((carMake) => carMake.name.toLowerCase() === car.make.trim().toLowerCase());
                        setCar((c) => ({ ...c, make: matchedMake?.name ?? '' }));
                        setUseCustomMake(false);
                      }}
                    >
                      select make
                    </button>
                  )}
                  <input
                    value={car.make}
                    onChange={(e) => setCar((c) => ({ ...c, make: e.target.value }))}
                    placeholder="e.g. Porsche"
                    required
                  />
                </>
              ) : (
                <select
                  value={makeSelectValue}
                  onChange={(e) => {
                    if (e.target.value === OTHER_MAKE_VALUE) {
                      setCar((c) => ({ ...c, make: '' }));
                      setUseCustomMake(true);
                      return;
                    }
                    if (e.target.value === CURRENT_MAKE_VALUE) return;
                    const nextMake = carMakes.find((carMake) => carMake.id === e.target.value);
                    setCar((c) => ({ ...c, make: nextMake?.name ?? '' }));
                  }}
                  className={styles.select}
                  disabled={carMakesLoading}
                  required
                >
                  <option value="">{carMakesLoading ? 'Loading makes...' : '— Select make —'}</option>
                  {car.make && !selectedMake && <option value={CURRENT_MAKE_VALUE}>{car.make}</option>}
                  {carMakes.map((carMake) => (
                    <option key={carMake.id} value={carMake.id}>{carMake.name}</option>
                  ))}
                  <option value={OTHER_MAKE_VALUE}>Other</option>
                </select>
              )}
            </label>
            {carMakesError && <p className={styles.hint}>Could not load car makes. Enter the make manually for now.</p>}
            <label>
              Model *
              <input
                value={car.model}
                onChange={(e) => setCar((c) => ({ ...c, model: e.target.value }))}
                placeholder="e.g. 911"
                required
              />
            </label>
            <label>
              Year range
              <input
                value={car.yearRange}
                onChange={(e) => setCar((c) => ({ ...c, yearRange: e.target.value }))}
                placeholder="e.g. 2019-2023"
              />
            </label>
            <label>
              Generation
              <input
                value={car.generation || ''}
                onChange={(e) => setCar((c) => ({ ...c, generation: e.target.value }))}
                placeholder="e.g. 992.1, E92, FK8"
              />
            </label>
            <label>
              Car type *
              <select
                value={car.bodyStyle || ''}
                onChange={(e) => setCar((c) => ({ ...c, bodyStyle: e.target.value }))}
                className={styles.select}
              >
                {CAR_TYPES.map((opt) => (
                  <option key={opt || 'none'} value={opt}>{opt || '— Select —'}</option>
                ))}
              </select>
            </label>
            <label>
              Performance type *
              <select
                value={car.performanceType || ''}
                onChange={(e) => setCar((c) => ({ ...c, performanceType: e.target.value }))}
                className={styles.select}
              >
                {PERFORMANCE_TYPES.map((opt) => (
                  <option key={opt || 'none'} value={opt}>{opt || '— Select —'}</option>
                ))}
              </select>
            </label>
            <label>
              Trim
              <input
                value={car.trim}
                onChange={(e) => setCar((c) => ({ ...c, trim: e.target.value }))}
                placeholder="e.g. GT3, Carrera S"
              />
            </label>
            <label>
              Mods
              <input
                value={car.mods}
                onChange={(e) => setCar((c) => ({ ...c, mods: e.target.value }))}
                placeholder="e.g. Lowered, exhaust"
              />
            </label>
            <label>
              Wheels
              <input
                value={car.wheels}
                onChange={(e) => setCar((c) => ({ ...c, wheels: e.target.value }))}
                placeholder="e.g. BBS, OEM"
              />
            </label>
            <label>
              Notes
              <input
                value={car.userNotes}
                onChange={(e) => setCar((c) => ({ ...c, userNotes: e.target.value }))}
                placeholder="Optional notes"
              />
            </label>
          </div>
        </div>

        <div className={styles.section}>
          <h2>Location & event</h2>
          <label>
            Country
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className={styles.select}
            >
              <option value="">— Select country —</option>
              {COUNTRIES_US_FIRST.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </label>
          <label>
            City
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Los Angeles" />
          </label>
          <label>
            Specific location
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Santa Monica Pier" />
          </label>
          <label>
            Event
            <input value={event} onChange={(e) => setEvent(e.target.value)} placeholder="e.g. Cars & Coffee" />
          </label>
          <label>
            Tag car owner
            <select
              value={taggedOwnerId}
              onChange={(e) => setTaggedOwnerId(e.target.value)}
              className={styles.select}
            >
              <option value="">None</option>
              {users.filter((u) => u.id !== user?.id).map((u) => (
                <option key={u.id} value={u.id}>{u.displayName} (@{u.username})</option>
              ))}
            </select>
          </label>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button type="submit" className={styles.submit} disabled={submitting}>
            {submitting ? 'Posting...' : 'Post spot'}
          </button>
          <button type="button" className={styles.cancel} onClick={() => navigate('/')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
